<template>
	<SuiSelectListHeaderWithSearch
		:search-query="searchQuery"
		:search-placeholder="searchPlaceholderText"
		class="user-assign-select-header"
		@close="emit('close')"
		@search="emit('search', $event)"
	>
		<template #title>
			{{ activeTitle }}
		</template>

		<template #close>
			{{ closeLabel }}
		</template>

		<template #content>
			<Transition name="user-assign-select-header__expand">
				<div
					v-if="totalItemsCount > 0"
					class="user-assign-select-header__content"
				>
					<div class="user-assign-select-header__wrapper">
						<div class="user-assign-select-header__inner">
							<div
								ref="$usersList"
								class="user-assign-select-header__list"
								:class="listClassAddons"
							>
								<template
									v-for="item in rolesSorted.slice(
										0,
										visibleRolesCount
									)"
									:key="item.id"
								>
									<UserAssignSelectHeaderRoleItem
										v-if="item.type === RoleSlotType.Role"
										:role="item.role"
										:allocated-time="
											allocatedTimeInputs.roles[
												item.role.id
											] ?? ''
										"
										:has-individual-time-allocation="
											hasIndividualTimeAllocation
										"
										:has-custom-allocation="
											hasCustomAllocation
										"
										:confirmed-allocated-times="
											confirmedAllocatedTimes
										"
										:active-role-filter-id="
											activeRoleFilterId
										"
										@update:allocated-time="
											allocatedTimeInputs.roles[
												item.role.id
											] = $event
										"
										@focus="
											currentlyFocusedInput = item.role.id
										"
										@blur="
											currentlyFocusedInput = undefined
										"
										@update:active-role-filter-id="
											emit(
												'update:active-role-filter-id',
												item.role.id
											)
										"
									/>
									<UserAssignSelectHeaderUserItem
										v-else
										:user="item.user"
										:availability="
											getUserAvailability(item.user.id)
										"
										:extra-used-availability="
											getExtraUsedAvailability(
												item.user.id
											)
										"
										:allocated-time="
											allocatedTimeInputs.users[
												item.user.id
											] ?? ''
										"
										:has-individual-time-allocation="
											hasIndividualTimeAllocation
										"
										:has-custom-allocation="
											hasCustomAllocation
										"
										:confirmed-allocated-times="
											confirmedAllocatedTimes
										"
										:active-role-filter-id="
											activeRoleFilterId
										"
										@update:allocated-time="
											allocatedTimeInputs.users[
												item.user.id
											] = $event
										"
										@focus="
											currentlyFocusedInput = item.user.id
										"
										@blur="
											currentlyFocusedInput = undefined
										"
										@unselect="
											emit('unselect', item.user.id)
										"
									/>
								</template>
								<UserAssignSelectHeaderUserItem
									v-for="user in usersSorted.slice(
										0,
										visibleUsersCount
									)"
									:key="user.id"
									:user="user"
									:availability="getUserAvailability(user.id)"
									:extra-used-availability="
										getExtraUsedAvailability(user.id)
									"
									:allocated-time="
										allocatedTimeInputs.users[user.id] ?? ''
									"
									:has-individual-time-allocation="
										hasIndividualTimeAllocation
									"
									:has-custom-allocation="hasCustomAllocation"
									:confirmed-allocated-times="
										confirmedAllocatedTimes
									"
									:active-role-filter-id="activeRoleFilterId"
									@update:allocated-time="
										allocatedTimeInputs.users[user.id] =
											$event
									"
									@focus="currentlyFocusedInput = user.id"
									@blur="currentlyFocusedInput = undefined"
									@unselect="emit('unselect', user.id)"
								/>
								<UserAssignSelectHeaderExpandedList
									:roles-sorted="rolesSorted"
									:users-sorted="usersSorted"
									:allocated-time-inputs="allocatedTimeInputs"
									:has-individual-time-allocation="
										hasIndividualTimeAllocation
									"
									:has-custom-allocation="hasCustomAllocation"
									:confirmed-allocated-times="
										confirmedAllocatedTimes
									"
									:active-role-filter-id="activeRoleFilterId"
									:task-users-availability="
										taskUsersAvailability
									"
									:extra-count="extraCount"
									:is-all-assignees-shown="
										isAllAssigneesShown
									"
									:dropdown-reference="dropdownReference"
									:users-list-element="$usersList"
									:show-more-label="$t('show_more')"
									:expanded-list-aria-label="
										$t('user_assign_expand_user_list')
									"
									:collapse-label="$t('collapse')"
									@update:role-allocated-time="
										(roleId, value) =>
											(allocatedTimeInputs.roles[roleId] =
												value)
									"
									@update:user-allocated-time="
										(userId, value) =>
											(allocatedTimeInputs.users[userId] =
												value)
									"
									@focus="currentlyFocusedInput = $event"
									@blur="currentlyFocusedInput = undefined"
									@update:active-role-filter-id="
										emit(
											'update:active-role-filter-id',
											$event
										)
									"
									@unselect="emit('unselect', $event)"
								/>
							</div>

							<div
								v-if="hasIndividualTimeAllocation"
								class="user-assign-select-header__toggle"
								:class="toggleClassAddons"
							>
								<SuiToggle
									:model-value="hasCustomAllocation"
									:disabled="!!activeRoleFilterId"
									:label="
										$t('avatars__individual_allocation')
									"
									@update:model-value="
										emit(
											'update:has-custom-allocation',
											$event
										)
									"
								/>
							</div>
						</div>
					</div>
				</div>
			</Transition>
		</template>
	</SuiSelectListHeaderWithSearch>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { SuiToggle, SuiSelectListHeaderWithSearch } from '@developers/scoro-ui';
import { useTranslation } from '@/composables';
import {
	type User,
	type UserAssignSelectAvailabilityQuery,
	type UserAssignSelectUserFragment,
} from '@/generated/graphql';
import {
	type TaskAssigneeInputDuration,
	type TaskAssigneeAllocatedDuration,
} from '@/features/Task/components/TaskUsers/types';
import { shortDurationValue } from '@/utils/formatting';
import { timeToSeconds } from '@/utils/time';
import UserAssignSelectHeaderUserItem from './UserAssignSelectHeaderUserItem.vue';
import UserAssignSelectHeaderRoleItem from './UserAssignSelectHeaderRoleItem.vue';
import UserAssignSelectHeaderExpandedList from './UserAssignSelectHeaderExpandedList.vue';
import { useCurrentUser } from '@/features/Auth';
import { sortSelectedRoles, sortSelectedUsers } from '../../../utils';
import {
	RoleSlotType,
	type PinnedUserPositions,
	type UserAssignSelectRole,
} from '../../../types';
import { MAX_AVATARS_SHOWN_WITHOUT_HIDING } from '../../../constants';

export interface UserAssignSelectHeaderProps {
	title?: string;
	searchQuery: string;
	enableRoleSelection?: boolean;
	usersList: UserAssignSelectUserFragment[];
	selectedUsers: Array<User['id']>;
	selectedRoles: UserAssignSelectRole[];
	pinnedUserPositions?: PinnedUserPositions;
	hasIndividualTimeAllocation?: boolean;
	hasCustomAllocation?: boolean;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
	allocatedTimes?: TaskAssigneeAllocatedDuration;
	taskUsersAvailability?: UserAssignSelectAvailabilityQuery['TaskUsersAvailability'];
	activeRoleFilterId: UserAssignSelectRole['id'] | null;
}

const {
	title = '',
	searchQuery,
	enableRoleSelection = false,
	usersList,
	selectedUsers,
	selectedRoles,
	pinnedUserPositions = new Map(),
	hasIndividualTimeAllocation = false,
	hasCustomAllocation = false,
	confirmedAllocatedTimes = undefined,
	allocatedTimes = undefined,
	taskUsersAvailability = [],
	activeRoleFilterId,
} = defineProps<UserAssignSelectHeaderProps>();

const emit = defineEmits<{
	close: [];
	search: [value: string];
	unselect: [userId: User['id']];
	'update:has-custom-allocation': [value: boolean];
	'update:allocated-times': [payload: TaskAssigneeInputDuration];
	'update:active-role-filter-id': [roleId: UserAssignSelectRole['id']];
}>();

const { t } = useTranslation();
const { currentUser } = useCurrentUser();

const $usersList = ref<HTMLDivElement | null>(null);
const allocatedTimeInputs = reactive<TaskAssigneeInputDuration>({
	users: {},
	roles: {},
});
const currentlyFocusedInput = ref<string>();

const searchPlaceholderText = computed(() =>
	enableRoleSelection
		? t('user_assign_select_find_user_or_role')
		: t('user_assign_select_find_user')
);

const users = computed(() => {
	return usersList.filter((user) => selectedUsers.includes(user.id));
});

const activeTitle = computed(() => {
	if (activeRoleFilterId) {
		const role = selectedRoles.find(
			(role) => role.id === activeRoleFilterId
		);
		return t('user_assign_select_assign_to', { role: role?.name ?? '' });
	}

	return title;
});

const closeLabel = computed(() => {
	if (activeRoleFilterId) {
		return t('cancel');
	}

	return t('close');
});

const rolesSorted = computed(() =>
	sortSelectedRoles({
		users: users.value,
		selectedRoles,
		pinnedUserPositions,
	})
);

const usersSorted = computed(() =>
	sortSelectedUsers({
		users: users.value,
		pinnedUserPositions,
		currentUserId: currentUser.value?.id,
	})
);

const totalItemsCount = computed(() => {
	return rolesSorted.value.length + usersSorted.value.length;
});

const listClassAddons = computed(() => ({
	'user-assign-select-header__list--with-spacing': totalItemsCount.value > 0,
}));

const toggleClassAddons = computed(() => ({
	'user-assign-select-header__toggle--disabled': !!activeRoleFilterId,
}));

const visibleRolesCount = computed(() => {
	return Math.min(rolesSorted.value.length, MAX_AVATARS_SHOWN_WITHOUT_HIDING);
});

const visibleUsersCount = computed(() => {
	return Math.max(
		0,
		MAX_AVATARS_SHOWN_WITHOUT_HIDING - visibleRolesCount.value
	);
});

const extraCount = computed(() => {
	const shouldShowExtraCount =
		totalItemsCount.value > MAX_AVATARS_SHOWN_WITHOUT_HIDING;

	if (!shouldShowExtraCount) {
		return 0;
	}

	const totalVisible = visibleRolesCount.value + visibleUsersCount.value;

	return totalItemsCount.value - totalVisible;
});

const isAllAssigneesShown = computed(() => {
	return extraCount.value <= 0;
});

const dropdownReference = () =>
	document.querySelector('.user-assign-select-header__list');

const getUserAvailability = (userId: UserAssignSelectUserFragment['id']) => {
	return taskUsersAvailability?.find(
		(availability) => availability.user.id === userId
	);
};

const getExtraUsedAvailability = (
	userId: UserAssignSelectUserFragment['id']
) => {
	const allocatedTimeInSeconds = timeToSeconds(
		allocatedTimeInputs.users[userId] ?? '0'
	);
	const confirmedTime = confirmedAllocatedTimes?.users.get(userId) ?? 0;

	return allocatedTimeInSeconds - confirmedTime;
};

watch(
	() => allocatedTimes,
	(newAllocatedTimes) => {
		if (!newAllocatedTimes) {
			return;
		}

		for (const user of selectedUsers) {
			const time = newAllocatedTimes.users.get(user);
			if (time !== undefined && currentlyFocusedInput.value !== user) {
				allocatedTimeInputs.users[user] = shortDurationValue(time);
			}
		}

		for (const role of selectedRoles) {
			const time = newAllocatedTimes.roles.get(role.id);
			if (time !== undefined && currentlyFocusedInput.value !== role.id) {
				allocatedTimeInputs.roles[role.id] = shortDurationValue(time);
			}
		}
	},
	{ immediate: true, deep: true }
);

watch(
	allocatedTimeInputs,
	(newAllocatedTimeInputs) =>
		emit('update:allocated-times', newAllocatedTimeInputs),
	{ deep: true }
);
</script>

<style lang="scss" scoped>
.user-assign-select-header {
	&__content {
		display: grid;
		overflow: hidden;
	}

	&__wrapper {
		min-height: 0;
	}

	&__inner {
		padding: 2rem 2rem 0;
	}

	&__list {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;

		&--with-spacing {
			margin-bottom: 1rem;
		}
	}

	&__toggle {
		display: inline-block;
		transition: opacity 200ms ease;

		&--disabled {
			opacity: 0.5;
			pointer-events: none;
		}
	}

	&__expand {
		&-enter-active,
		&-leave-active {
			transition: grid-template-rows 0.3s ease-in-out;
		}

		&-enter-from,
		&-leave-to {
			grid-template-rows: 0fr;
		}

		&-enter-to,
		&-leave-from {
			grid-template-rows: 1fr;
		}
	}
}
</style>
